import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Upload, Edit2, Trash2, LogOut, Image as ImageIcon, Save, X, Languages, Plus, BarChart3, GripVertical, Inbox } from 'lucide-react';
import { SortableList, SortableItem } from '../components/Sortable';
import { toast } from 'sonner';
import axios from 'axios';

const CATEGORIES = [
  { value: 'metalwork', key: 'metalwork' },
  { value: 'print3d', key: 'print3d' },
  { value: 'engraving', key: 'engraving' },
  { value: 'scaffolding', key: 'scaffolding' },
];

// Inline safe text (no external import)
const toStr = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    if (v.cs) return v.cs;
    for (const k of Object.keys(v)) {
      if (typeof v[k] === 'string' && v[k]) return v[k];
    }
    return '';
  }
  return String(v);
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadMode, setUploadMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [addImagesItemId, setAddImagesItemId] = useState(null);
  const [newInquiries, setNewInquiries] = useState(0);

  useEffect(() => {
    axios.get('/api/admin/inquiries/count').then(({ data }) => setNewInquiries(data.new)).catch(() => {});
  }, []);

  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    category: 'metalwork',
    files: [],
  });

  const [editData, setEditData] = useState({
    title: '',
    description: '',
    category: 'metalwork',
  });

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const { data } = await axios.get('/api/gallery');
      // Normalize items defensively
      const safeItems = (Array.isArray(data) ? data : []).map((item) => ({
        id: item.id,
        title: toStr(item.title),
        description: toStr(item.description),
        category: typeof item.category === 'string' ? item.category : 'metalwork',
        images: Array.isArray(item.images) ? item.images : (item.image_url ? [item.image_url] : []),
      }));
      setGalleryItems(safeItems);
    } catch (error) {
      toast.error(t('admin.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.files || uploadData.files.length === 0) {
      toast.error('Vyberte alespoň jeden obrázek');
      return;
    }

    const formData = new FormData();
    formData.append('title', uploadData.title);
    formData.append('description', uploadData.description);
    formData.append('category', uploadData.category);
    Array.from(uploadData.files).forEach((file) => formData.append('files', file));

    try {
      await axios.post('/api/admin/gallery', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(`${t('admin.uploadSuccess')} (${uploadData.files.length} obrázků)`);
      setUploadMode(false);
      setUploadData({ title: '', description: '', category: 'metalwork', files: [] });
      fetchGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.uploadError'));
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditData({
      title: item.title,
      description: item.description || '',
      category: item.category || 'metalwork',
    });
  };

  const handleUpdate = async (itemId) => {
    try {
      await axios.put(`/api/admin/gallery/${itemId}`, editData);
      toast.success(t('admin.updateSuccess'));
      setEditingId(null);
      fetchGallery();
    } catch (error) {
      toast.error(t('admin.updateError'));
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm(t('admin.confirmDelete'))) return;

    try {
      await axios.delete(`/api/admin/gallery/${itemId}`);
      toast.success(t('admin.deleteSuccess'));
      fetchGallery();
    } catch (error) {
      toast.error(t('admin.deleteError'));
    }
  };

  const handleAddImages = async (itemId, files) => {
    if (!files || files.length === 0) return;
    const formData = new FormData();
    Array.from(files).forEach((f) => formData.append('files', f));

    try {
      await axios.post(`/api/admin/gallery/${itemId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Přidáno ${files.length} obrázků`);
      setAddImagesItemId(null);
      fetchGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při přidávání obrázků');
    }
  };

  const handleRemoveImage = async (itemId, imageIndex) => {
    if (!window.confirm('Smazat tento obrázek?')) return;
    try {
      await axios.delete(`/api/admin/gallery/${itemId}/images/${imageIndex}`);
      toast.success('Obrázek smazán');
      fetchGallery();
    } catch (error) {
      toast.error('Chyba při mazání obrázku');
    }
  };

  const handleReorderItems = async (newIds) => {
    const slots = galleryItems.map((it, i) => (newIds.includes(it.id) ? i : -1)).filter((i) => i >= 0);
    const next = [...galleryItems];
    newIds.forEach((id, k) => { next[slots[k]] = galleryItems.find((it) => it.id === id); });
    setGalleryItems(next);
    try {
      await axios.put('/api/admin/gallery/reorder', { items: next.map((it, i) => ({ id: it.id, order: i })) });
      toast.success('Pořadí uloženo');
    } catch (error) {
      toast.error('Chyba při ukládání pořadí');
      fetchGallery();
    }
  };

  const handleReorderImages = async (item, newUrls) => {
    const order = newUrls.map((u) => item.images.indexOf(u));
    setGalleryItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, images: newUrls } : it)));
    try {
      await axios.put(`/api/admin/gallery/${item.id}/images/order`, { order });
      toast.success('Pořadí fotek uloženo');
    } catch (error) {
      toast.error('Chyba při ukládání pořadí fotek');
      fetchGallery();
    }
  };

  const filteredItems = activeTab === 'all'
    ? galleryItems
    : galleryItems.filter((item) => (item.category || 'metalwork') === activeTab);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('admin.dashboardTitle')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
              {t('admin.loggedInAs')} {user?.email}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/admin/inquiries"
              data-testid="admin-inquiries-link"
              className="relative flex items-center space-x-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <Inbox size={20} />
              <span>Poptávky</span>
              {newInquiries > 0 && (
                <span data-testid="admin-inquiries-new-count" className="ml-1 bg-red-600 text-white text-xs font-bold rounded-full px-2 py-0.5">
                  {newInquiries} {newInquiries === 1 ? 'nová' : newInquiries <= 4 ? 'nové' : 'nových'}
                </span>
              )}
            </Link>
            <Link
              to="/admin/analytics"
              data-testid="admin-analytics-link"
              className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <BarChart3 size={20} />
              <span>Návštěvnost</span>
            </Link>
            <Link
              to="/admin/translations"
              data-testid="admin-translations-link"
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <Languages size={20} />
              <span>Správa překladů</span>
            </Link>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <LogOut size={20} />
              <span>{t('admin.logout')}</span>
            </button>
          </div>
        </div>

        {!uploadMode && (
          <button
            onClick={() => setUploadMode(true)}
            data-testid="admin-show-upload-btn"
            className="mb-6 flex items-center space-x-2 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white dark:text-black font-semibold px-6 py-3 rounded-lg transition-all duration-200 shadow-lg"
          >
            <Upload size={20} />
            <span>{t('admin.uploadNew')}</span>
          </button>
        )}

        {uploadMode && (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-8 shadow-lg" data-testid="admin-upload-form">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('admin.uploadNew')}</h2>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-gray-900 dark:text-white font-medium mb-2">{t('admin.uploadTitle')}</label>
                <input
                  type="text"
                  value={uploadData.title}
                  onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                  required
                  data-testid="admin-upload-title"
                  className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-gray-900 dark:text-white font-medium mb-2">{t('admin.uploadDesc')}</label>
                <textarea
                  value={uploadData.description}
                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                  rows={3}
                  data-testid="admin-upload-desc"
                  className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-gray-900 dark:text-white font-medium mb-2">{t('admin.uploadCategory')}</label>
                <select
                  value={uploadData.category}
                  onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })}
                  data-testid="admin-upload-category"
                  className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-green-500"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {t(`gallery.filters.${cat.key}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-900 dark:text-white font-medium mb-2">
                  Obrázky (můžete vybrat více najednou, max 10)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setUploadData({ ...uploadData, files: e.target.files })}
                  required
                  data-testid="admin-upload-file"
                  className="w-full text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-100 file:text-green-700 hover:file:bg-green-200"
                />
                {uploadData.files && uploadData.files.length > 0 && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                    Vybráno: {uploadData.files.length} {uploadData.files.length === 1 ? 'obrázek' : 'obrázků'}
                  </p>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  data-testid="admin-upload-submit"
                  className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white dark:text-black px-6 py-2 rounded-lg font-semibold"
                >
                  <Upload size={18} />
                  <span>{t('admin.uploadBtn')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMode(false)}
                  data-testid="admin-upload-cancel"
                  className="flex items-center space-x-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white px-6 py-2 rounded-lg"
                >
                  <X size={18} />
                  <span>{t('admin.cancel')}</span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-category-tabs">
          <button
            onClick={() => setActiveTab('all')}
            data-testid="admin-tab-all"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              activeTab === 'all'
                ? 'bg-green-600 dark:bg-green-500 text-white dark:text-black'
                : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
            }`}
          >
            {t('admin.filterAll')} ({galleryItems.length})
          </button>
          {CATEGORIES.map((cat) => {
            const count = galleryItems.filter((i) => (i.category || 'metalwork') === cat.value).length;
            return (
              <button
                key={cat.value}
                onClick={() => setActiveTab(cat.value)}
                data-testid={`admin-tab-${cat.value}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === cat.value
                    ? 'bg-green-600 dark:bg-green-500 text-white dark:text-black'
                    : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                }`}
              >
                {t(`gallery.filters.${cat.key}`)} ({count})
              </button>
            );
          })}
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="text-center text-gray-600 dark:text-gray-400 py-20">...</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-20 text-gray-600 dark:text-gray-400">
            <ImageIcon size={64} className="mx-auto mb-4 opacity-30" />
            <p>{t('admin.noImages')}</p>
          </div>
        ) : (
          <>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4" data-testid="admin-reorder-hint">
            Pořadí změníte přetažením karty za úchyt <GripVertical size={14} className="inline" />. Fotky v projektu přetáhněte v pásu náhledů – první fotka je titulní.
          </p>
          <SortableList ids={filteredItems.map((i) => i.id)} onReorder={handleReorderItems}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => {
              const imgCount = item.images.length;
              const mainImage = item.images[0] || '';
              return (
                <SortableItem
                  key={item.id}
                  id={item.id}
                  testId={`admin-gallery-item-${item.id}`}
                  className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-lg"
                >
                  {({ handleProps, isDragging }) => (
                  <>
                  <div className="aspect-square overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center relative">
                    <button
                      type="button"
                      {...handleProps}
                      data-testid={`admin-drag-handle-${item.id}`}
                      title="Přetáhněte pro změnu pořadí"
                      className={`absolute top-2 left-2 z-10 bg-black/70 hover:bg-black text-white p-2 rounded-lg touch-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                    >
                      <GripVertical size={18} />
                    </button>
                    {mainImage ? (
                      <img
                        src={mainImage}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageIcon size={48} className="text-gray-400" />
                    )}
                    {imgCount > 1 && (
                      <div className="absolute top-2 right-2 bg-black/70 text-white text-xs font-bold px-2 py-1 rounded-full">
                        {imgCount} fotek
                      </div>
                    )}
                  </div>

                  {/* Thumbnail strip for additional images */}
                  {imgCount > 1 && (
                    <SortableList ids={item.images} horizontal onReorder={(urls) => handleReorderImages(item, urls)}>
                    <div className="flex gap-1 p-2 overflow-x-auto bg-gray-50 dark:bg-gray-800" data-testid={`thumb-strip-${item.id}`}>
                      {item.images.map((url, idx) => (
                        <SortableItem key={url} id={url} testId={`thumb-${item.id}-${idx}`} className="relative flex-shrink-0 group">
                          {({ handleProps: thumbHandle }) => (
                          <>
                          <img
                            src={url}
                            alt={`${item.title} ${idx + 1}`}
                            {...thumbHandle}
                            draggable={false}
                            className={`w-14 h-14 object-cover rounded border cursor-grab touch-none ${idx === 0 ? 'border-green-500 border-2' : 'border-gray-300 dark:border-gray-700'}`}
                          />
                          <button
                            onClick={() => handleRemoveImage(item.id, idx)}
                            data-testid={`remove-img-${item.id}-${idx}`}
                            className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Smazat tento obrázek"
                          >
                            <X size={12} />
                          </button>
                          </>
                          )}
                        </SortableItem>
                      ))}
                    </div>
                    </SortableList>
                  )}

                  <div className="p-4">
                    {editingId === item.id ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          data-testid={`admin-edit-title-${item.id}`}
                          value={editData.title}
                          onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                          className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white text-sm"
                        />
                        <textarea
                          value={editData.description}
                          onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                          rows={2}
                          className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white text-sm"
                        />
                        <select
                          value={editData.category}
                          onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                          className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white text-sm"
                        >
                          {CATEGORIES.map((cat) => (
                            <option key={cat.value} value={cat.value}>
                              {t(`gallery.filters.${cat.key}`)}
                            </option>
                          ))}
                        </select>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleUpdate(item.id)}
                            data-testid={`admin-edit-save-${item.id}`}
                            className="flex-1 flex items-center justify-center space-x-1 bg-green-600 text-white px-3 py-2 rounded text-sm"
                          >
                            <Save size={14} />
                            <span>{t('admin.save')}</span>
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            data-testid={`admin-edit-cancel-${item.id}`}
                            className="flex-1 flex items-center justify-center space-x-1 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 rounded text-sm"
                          >
                            <X size={14} />
                            <span>{t('admin.cancel')}</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-gray-900 dark:text-white font-semibold mb-1">
                          {item.title || <em className="text-gray-400">(bez názvu)</em>}
                        </h3>
                        {item.description && (
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 line-clamp-2">
                            {item.description}
                          </p>
                        )}
                        <p className="text-xs text-green-600 dark:text-green-400 font-medium mb-3">
                          {t(`gallery.filters.${item.category || 'metalwork'}`)}
                        </p>

                        {/* Add images button + hidden file input */}
                        {addImagesItemId === item.id ? (
                          <div className="mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              data-testid={`add-images-input-${item.id}`}
                              onChange={(e) => handleAddImages(item.id, e.target.files)}
                              className="w-full text-xs text-gray-900 dark:text-white file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-green-100 file:text-green-700"
                            />
                            <button
                              onClick={() => setAddImagesItemId(null)}
                              className="mt-1 text-xs text-gray-500"
                            >
                              Zrušit
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setAddImagesItemId(item.id)}
                            data-testid={`add-images-${item.id}`}
                            className="w-full mb-2 flex items-center justify-center space-x-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50 px-3 py-2 rounded text-sm"
                          >
                            <Plus size={14} />
                            <span>Přidat další obrázky</span>
                          </button>
                        )}

                        <div className="flex space-x-2">
                          <button
                            onClick={() => startEdit(item)}
                            data-testid={`admin-edit-${item.id}`}
                            className="flex-1 flex items-center justify-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm"
                          >
                            <Edit2 size={14} />
                            <span>{t('admin.edit')}</span>
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            data-testid={`admin-delete-${item.id}`}
                            className="flex-1 flex items-center justify-center space-x-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm"
                          >
                            <Trash2 size={14} />
                            <span>{t('admin.delete')}</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  </>
                  )}
                </SortableItem>
              );
            })}
          </div>
          </SortableList>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
